import { useEffect, useState } from 'react';
import { Mail, Loader2, Send, History, RotateCcw, Search, Download, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { API, getAuthHeaders } from '../../services/http';
import { EmailArchiveHistory } from './EmailArchiveHistory';
import { I18nEmailPreviews } from './I18nEmailPreviews';

const EmailLogsList = ({ templateId }) => {
  const [logs, setLogs] = useState(null);
  const [resending, setResending] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLogs(null);
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      fetch(`${API}/admin/email-previews/${templateId}/logs?q=${encodeURIComponent(search)}`, {
        headers: getAuthHeaders(), credentials: 'include', signal: ctl.signal,
      })
        .then((r) => r.json())
        .then((d) => setLogs(d.logs || []))
        .catch(() => {});
    }, search ? 300 : 0);
    return () => { clearTimeout(timer); ctl.abort(); };
  }, [templateId, search]);

  const resend = async (log) => {
    setResending(log.id);
    try {
      const r = await fetch(`${API}/admin/email-previews/logs/${log.id}/resend`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Renvoi impossible');
      toast.success(`Email renvoyé à ${d.to}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResending(null);
    }
  };

  return (
    <div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un destinataire…"
          data-testid="email-logs-search"
          className="h-8 w-full pl-8 pr-3 rounded-lg bg-white/[0.06] border border-white/15 text-xs text-white placeholder:text-white/40 focus:border-[#D9B35A] outline-none"
        />
      </div>
      {logs === null ? (
        <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-[#D9B35A]" /></div>
      ) : logs.length === 0 ? (
        <p className="text-xs text-white/40 py-2" data-testid="email-logs-empty">
          {search ? `Aucun envoi trouvé pour « ${search} ».` : 'Aucun envoi réel journalisé pour ce modèle.'}
        </p>
      ) : (
      <div className="max-h-40 overflow-y-auto space-y-1" data-testid="email-logs-list">
      {logs.map((l, i) => (
        <div key={l.id || i} className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg bg-white/[0.04] text-xs">
          <span className="text-white/80 truncate">{l.to_email}</span>
          <span className="flex items-center gap-2 flex-shrink-0">
            <span className="text-white/45">
              {l.sent_at ? new Date(l.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
            {l.id && (
              <button
                type="button"
                onClick={() => resend(l)}
                disabled={resending === l.id}
                title="Renvoyer cet email au destinataire"
                data-testid={`resend-email-${l.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold transition-colors hover:brightness-110 disabled:opacity-50"
                style={{ background: 'rgba(217,179,90,0.16)', border: '1px solid rgba(217,179,90,0.45)', color: '#E9CF8E' }}
              >
                {resending === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Renvoyer
              </button>
            )}
          </span>
        </div>
      ))}
      </div>
      )}
    </div>
  );
};

export const EmailPreviewsTab = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [totalSent, setTotalSent] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveRefresh, setArchiveRefresh] = useState(0);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API}/admin/email-previews/export/csv`, { headers: getAuthHeaders(), credentials: 'include' });
      if (!r.ok) throw new Error('Export impossible');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `journal-emails-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Journal exporté en CSV');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const archiveGed = async () => {
    setArchiving(true);
    try {
      const r = await fetch(`${API}/admin/email-previews/archive-ged`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ force: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Archivage impossible');
      const msg = {
        SUCCESS: `Archivé dans la GED ESS (${d.rows} envois — ${d.month})`,
        EMPTY: `Aucun envoi à archiver pour ${d.month}`,
        GED_DISABLED: 'GED ESS non configurée',
        ERROR: `Erreur GED : ${d.error || ''}`,
      }[d.status] || d.status;
      (d.status === 'SUCCESS' ? toast.success : toast.warning)(msg);
      setArchiveRefresh((n) => n + 1);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setArchiving(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/admin/email-previews`, { headers: getAuthHeaders(), credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        setTemplates(d.templates || []);
        setSelected((d.templates || [])[0] || null);
        setTestEmail(d.admin_email || '');
        setTotalSent(d.total_sent || 0);
      })
      .finally(() => setLoading(false));
  }, []);

  const sendTest = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/admin/email-previews/${selected.id}/send-test`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: testEmail }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Envoi impossible');
      toast.success(`Email test envoyé à ${d.to}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#D9B35A]" /></div>;
  }

  const categories = [...new Set(templates.map((t) => t.category))];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-testid="email-previews-tab">
      <div className="glass-panel-soft rounded-[18px] p-4 max-h-[75vh] overflow-y-auto">
        <h3 className="flex items-center gap-2 text-sm font-semibold mb-1 text-[#D9B35A]">
          <Mail className="w-4 h-4" /> Modèles d'emails ({templates.length})
        </h3>
        <p className="text-[11px] text-white/45 mb-2" data-testid="email-total-sent">
          {totalSent.toLocaleString('fr-FR')} envoi{totalSent > 1 ? 's' : ''} réel{totalSent > 1 ? 's' : ''} journalisé{totalSent > 1 ? 's' : ''}
        </p>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="export-csv-btn"
            className="flex-1 h-8 inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors hover:brightness-110 disabled:opacity-50"
            style={{ background: 'rgba(217,179,90,0.16)', border: '1px solid rgba(217,179,90,0.45)', color: '#E9CF8E' }}
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Exporter CSV
          </button>
          <button
            type="button"
            onClick={archiveGed}
            disabled={archiving}
            data-testid="archive-ged-btn"
            title="Archiver le mois courant dans la GED ESS (automatique chaque 1er du mois)"
            className="flex-1 h-8 inline-flex items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors hover:brightness-110 disabled:opacity-50"
            style={{ background: 'rgba(217,179,90,0.16)', border: '1px solid rgba(217,179,90,0.45)', color: '#E9CF8E' }}
          >
            {archiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
            Archiver GED
          </button>
        </div>
        <I18nEmailPreviews />
        <EmailArchiveHistory refreshKey={archiveRefresh} title="Journal des emails archivé (GEDESS)" />
        <EmailArchiveHistory
          refreshKey={archiveRefresh}
          endpoint="/admin/compliance-report/archive-ged/runs"
          title="Rapports de conformité archivés (GEDESS)"
          testId="compliance-archive-history"
        />
        <EmailArchiveHistory
          refreshKey={archiveRefresh}
          endpoint="/rar/stats/admin/annual-archive/runs"
          title="Relevés annuels de plafond archivés (GEDESS)"
          testId="rar-annual-archive-history"
          unitLabel="relevés"
        />
        {categories.map((cat) => (
          <div key={cat} className="mb-3">
            <p className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">{cat}</p>
            <div className="space-y-1">
              {templates.filter((t) => t.category === cat).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected(t)}
                  data-testid={`email-template-${t.id}`}
                  className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selected?.id === t.id
                      ? 'bg-[#D9B35A]/20 text-[#D9B35A] border border-[#D9B35A]/40'
                      : 'text-white/75 hover:bg-white/[0.06] border border-transparent'
                  }`}
                >
                  <span className="truncate">{t.name}</span>
                  <span
                    data-testid={`email-count-${t.id}`}
                    className={`flex-shrink-0 min-w-[26px] text-center text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                      (t.stats?.count || 0) > 0
                        ? 'bg-[#D9B35A]/25 text-[#E9CF8E]'
                        : 'bg-white/[0.06] text-white/35'
                    }`}
                  >
                    {t.stats?.count || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="glass-panel-soft rounded-[18px] p-4">
        {selected ? (
          <>
            <div className="mb-3 pb-3 border-b border-white/10 flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-white/50">Objet</p>
                <p className="text-sm font-medium" data-testid="email-preview-subject">{selected.subject}</p>
                <p className="text-[11px] text-white/45 mt-0.5" data-testid="email-preview-stats">
                  {selected.stats?.count || 0} envoi{(selected.stats?.count || 0) > 1 ? 's' : ''} réel{(selected.stats?.count || 0) > 1 ? 's' : ''}
                  {selected.stats?.last_sent && ` · dernier : ${new Date(selected.stats.last_sent).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="votre@email.fr"
                  data-testid="test-email-input"
                  className="h-9 w-56 px-3 rounded-lg bg-white/[0.06] border border-white/15 text-sm text-white placeholder:text-white/40 focus:border-[#D9B35A] outline-none"
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={sending || !testEmail}
                  data-testid="send-test-email-btn"
                  className="h-9 inline-flex items-center gap-1.5 px-4 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ background: '#D4AF37', color: '#1F0A33' }}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  M'envoyer un test
                </button>
              </div>
            </div>
            <div className="mb-3 pb-3 border-b border-white/10">
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#D9B35A] mb-1.5">
                <History className="w-3.5 h-3.5" /> Journal des envois
              </p>
              <EmailLogsList templateId={selected.id} />
            </div>
            <iframe
              title="email-preview"
              srcDoc={selected.html}
              data-testid="email-preview-frame"
              className="w-full rounded-xl border border-white/10 bg-[#1E0C34]"
              style={{ height: '65vh' }}
            />
          </>
        ) : (
          <p className="text-white/50 text-sm">Sélectionnez un modèle</p>
        )}
      </div>
    </div>
  );
};
